import { useQuery } from '@tanstack/react-query';

import { readLead } from '@/api/leads';
import { proposalRequest, type Proposal } from '@/api/proposals';
import { useAuth } from '@/auth/useAuth';
import { recordForPath, scopeForPath, type RecordScope } from './AssistantScope';

interface RecordScopeOptions {
  /** Whether anyone is looking, so that a closed assistant costs no request. */
  readonly enabled: boolean;
}

/**
 * What the assistant is looking at on the page shown.
 *
 * A deal, a customer and a job are read as they are. A lead or a proposal has no scope of its own
 * on the server, but each belongs to a deal and that is what the assistant reads, so the deal is
 * found through the same query the page itself runs, which makes it a cache hit rather than a
 * request. The record is named for the page it was found on ("this lead’s deal"), so that the
 * button never claims to summarize something it does not.
 *
 * @param pathname the current location
 * @param options see {@link RecordScopeOptions}
 * @returns the scope, or null when the page is not a record's or its deal is not known yet
 */
export function useRecordScope(
  pathname: string,
  { enabled }: RecordScopeOptions,
): RecordScope | null {
  const { identity } = useAuth();
  const record = recordForPath(pathname);
  const leadId = enabled && record?.kind === 'lead' ? record.id : '';
  const proposalId = enabled && record?.kind === 'proposal' ? record.id : '';
  const signedIn = identity !== null;

  const lead = useQuery({
    queryKey: ['leads', identity, leadId],
    queryFn: () => readLead(leadId),
    enabled: signedIn && leadId !== '',
  });
  const proposal = useQuery({
    queryKey: ['proposal', identity, proposalId],
    queryFn: () => proposalRequest<Proposal>(`/${proposalId}`),
    enabled: signedIn && proposalId !== '',
  });

  const direct = scopeForPath(pathname);
  if (direct !== null) {
    return direct;
  }
  // A service request opens no deal, so its lead has nothing for the assistant to scope to.
  if (leadId !== '' && lead.data?.opportunityId !== undefined) {
    return { type: 'OPPORTUNITY', id: lead.data.opportunityId, noun: 'lead’s deal' };
  }
  if (proposalId !== '' && proposal.data !== undefined) {
    return { type: 'OPPORTUNITY', id: proposal.data.opportunityId, noun: 'proposal’s deal' };
  }
  return null;
}
